import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './Orbit.css';

export type OrbitTarget = 'united' | 'mesa' | 'ktp' | 'polymarket';

interface OrbitProject {
  title: string;
  year: number;
  /* Either an in-app detail page or an external site opened in a new tab. */
  page?: OrbitTarget;
  url?: string;
  image: string;
  position: string;
  /* Matte behind the contained photo; defaults to the CSS white when omitted. */
  bg?: string;
  /* 'contain' (default) fits the whole photo inside the card, letting the matte
     show through on whichever axis has slack. 'cover' fills the card edge to
     edge on both axes, cropping the photo instead — for a source image whose
     aspect ratio leaves too much matte showing (e.g. wide relative to the card,
     so contain fills the width but leaves visible bars above/below). */
  fit?: 'contain' | 'cover';
  /* A live, non-interactive embed of the project's own site, shown over the
     still (which becomes its load-time poster). Rendered at a desktop viewport
     and scaled to cover the card; pointer-events stay off so the card is inert. */
  embed?: string;
  /* Per-project override of EMBED_ZOOM — how big this page sits in the card.
     Omit to inherit the default inset; >1 fills to full-bleed (edges cropped). */
  embedZoom?: number;
  /* Fraction of the card's height to clip off the TOP of the embed (e.g. a site's
     fixed header). The page is grown just enough that its bottom stays flush, so
     only the top band is lost — nothing mattes in below. */
  embedCropTop?: number;
  /* Mirror of embedCropTop for the BOTTOM: fraction of the card's height to clip
     off the bottom of the embed (e.g. a site's empty run-out below its landing
     content). The page is grown until its top stays flush, so only the bottom
     band is lost — nothing mattes in above. Doubles as a zoom-in: growing the
     page to fill the card height crops the vertical matte too. */
  embedCropBottom?: number;
}

const PROJECTS: OrbitProject[] = [
  { title: 'Tune Capsule', year: 2026, url: 'https://tunecapsule.io', image: '/TCHero.png', position: '50% 42%', bg: '#000000', fit: 'cover' },
  { title: 'Polymarket Ideas', year: 2026, page: 'polymarket', image: '/polymarket-cover.png', position: '50% 50%' },
  { title: 'Know That Plane', year: 2026, url: 'https://knowthatplane.com', image: '/KTPHero.png', position: '72% 50%', bg: '#EBF0F3', embed: 'https://www.knowthatplane.com', embedCropBottom: 0.14 },
  /* Shelved, not retired: the United case study and its /united route are intact
     (UnitedPage, ProjectReveal wiring, assets) — it's just out of the orbit for
     now, with Polymarket standing in its place. Re-add this line to bring it back. */
  // { title: 'United Airlines Mobile Redesign', year: 2025, page: 'united', image: '/united-total.png', position: '50% 50%' },
  { title: 'mesa', year: 2025, url: 'https://mesa-seating.vercel.app', image: '/mesa-landing.png', position: '50% 58%' },
];

const N = PROJECTS.length;

/* Live embeds render each site at a fixed desktop viewport, contain-fit into the
   card. The viewport is deliberately WIDER than the 3:2 card (≈5:3) so the site
   lays out with desktop horizontal room instead of a scrunched near-square; the
   fit then becomes width-driven, so EMBED_ZOOM sets how much of the card WIDTH the
   page spans. Keep in sync with .orbit-embed's width/height in Orbit.css. */
const EMBED_W = 1600;
const EMBED_H = 960;
/* Fraction of the card's constrained (here: width) dimension the fitted page
   spans, after contain. 1 = edge-to-edge; below 1 insets the page evenly, leaving
   a matte margin. Scales the final rendered result, so it's the monotonic "how big
   in the frame" knob — raise it to widen the page. It's independent of EMBED_W,
   which sets how wide-angle the site's own layout is (bigger = more horizontal
   room but smaller-rendered content). */
const EMBED_ZOOM = 0.9;

/* Which slot a routable project occupies — lets the app land the orbit on the
   right work when a detail view is entered by URL rather than by tap. */
export const orbitIndexOf = (page: OrbitTarget): number =>
  Math.max(0, PROJECTS.findIndex((p) => p.page === page));

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/* The full-size card's pixel box for a given viewport — the single source of
   truth the rAF loop's geometry and the live-embed scaler both read, so the two
   can never drift. */
const cardMetrics = (vw: number, vh: number) => {
  /* Portrait phones (the 640px breakpoint the rest of the component tracks): hold
     the card at the SAME 3:2 landscape as desktop, driven by width, instead of
     letting it grow tall and portrait. A portrait frame fought every preview —
     they're all landscape (1.4–2.1) — so cover cropped the sides and contain
     letterboxed the top/bottom. A landscape frame fits them the way desktop does.
     Height is capped against the viewport so a short phone never crowds the HUD or
     the placard. */
  if (vw <= 640) {
    const cardW = clamp(vw * 0.9, 220, 940);
    const cardH = clamp(cardW / 1.5, 150, vh * 0.62);
    return { cardW, cardH };
  }
  const cardH = clamp(Math.min(vh * 0.7, vw * 1.05), 200, 620);
  const cardW = clamp(Math.min(cardH * 1.5, vw * 0.86), 220, 940);
  return { cardW, cardH };
};

/* Signed distance from a work to the current view, clamped to the half-turn on
   either side. The track is a bounded arc, not a ring: works never wrap around,
   so the one four slots away stays four slots away (parked behind you) instead
   of sneaking in from the other side. Clamping to ±N/2 also keeps it on the
   monotonic half of the cosine, so a distant work always reads as "behind you"
   and never trig-wraps back into view. */
const drawOffset = (raw: number) => clamp(raw, -N / 2, N / 2);

interface OrbitProps {
  onOpen: (page: OrbitTarget) => void;
  /* Slot to stand facing on mount (read once) — deep-linking a detail view
     lands the orbit on that work so the view can contract back onto it. */
  initialIndex?: number;
  /* True while a detail view covers the orbit: the keyboard is theirs. */
  suspended?: boolean;
}

/* You stand at the centre of an ellipse with the works orbiting you like planets
   — the sun watching its system turn. The one you face is closest and shown full
   size; the rest ride the ellipse out to your periphery, shrinking to small black
   dots, and vanish once they swing behind you. Scrolling rotates your gaze: an
   approaching planet arcs inward off the rim and blooms open into the full still
   while the one you're leaving curves away into a dot. The track runs from the
   first work to the last and stops — nothing before the first, nothing after
   the last.

   Each work is a single black shape that genuinely morphs — dot ⇄ card — the way
   the Dynamic Island does, and with the Island's physics: nothing runs on a
   clock. The size the scroll position asks for is only a *target*; what's
   painted is the state of springs (Hooke's law) chasing it, so the shape always
   carries momentum. The stiff spring swaps sides with direction: opening,
   width leads and the card stretches wide first, filling tall a beat later;
   closing, height leads and the card melts through a wide pill before it
   contracts — mid-morph the shape is always landscape, like the previews.
   Expansion is underdamped (it overshoots and settles back), collapse
   near-critically damped (it shuts without clattering). Corner radius stays continuous —
   a capsule while small, easing into the card's 16px — and fast travel
   stretches a dot along its path like a thrown drop. The photo lifts in
   trailing the shape, content following the container.

   To keep that smooth with 3–4K source images the morph
   is split into cheap layers: the black shape resizes (a solid fill, no image, no
   blur), the photo is a fixed-size layer that is only ever transform-scaled and
   clipped (so it's rasterised once, never re-scaled), and the drop shadow is its
   own scaled layer so its blur never repaints. Per frame the loop writes only
   transform / opacity (and the shape's size) — never anything that reflows the
   page or re-rasterises a photo. React re-renders only when the focus changes. */
const Orbit: React.FC<OrbitProps> = ({ onOpen, initialIndex = 0, suspended = false }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const morphRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const fillRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const imgRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const embedRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const shadowRefs = useRef<Array<HTMLSpanElement | null>>([]);

  const [focused, setFocused] = useState(initialIndex);

  /* Live embeds sit black under the dot fill until their card blooms, so
     nothing is lost by not loading them during the intro — and loading a whole
     external site while the settle is playing is main-thread contention the
     first frames can't afford. Mount their src only once the intro is done. */
  const [embedsLive, setEmbedsLive] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setEmbedsLive(true), 900);
    return () => clearTimeout(t);
  }, []);

  const s = useRef({
    view: initialIndex,
    viewVel: 0,
    target: null as number | null,
    introStart: 0,
    focused: initialIndex,
    interacted: false,
    reduced: false,
    mobile: false,
    gap: 320,
  });

  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;

  const markInteracted = useCallback(() => {
    if (!s.current.interacted) {
      s.current.interacted = true;
    }
  }, []);

  /* Nudge the track by whole slots. `dir = +1` advances to the next project
     (the right-hand dot blooms into the centre); -1 pulls the previous one back
     in from the left. Clamped to the ends: stepping past the last work or before
     the first is a no-op. */
  const stepBy = useCallback((dir: number) => {
    const st = s.current;
    const from = st.target != null ? Math.round(st.target) : Math.round(st.view);
    st.target = clamp(from + dir, 0, N - 1);
  }, []);

  /* Open a project: an external site rides out to a new tab, an in-app work
     routes to its detail page. */
  const openProject = useCallback((i: number) => {
    const p = PROJECTS[i];
    if (p.url) window.open(p.url, '_blank', 'noopener,noreferrer');
    else if (p.page) onOpen(p.page);
  }, [onOpen]);

  /* A tap on the centred still opens it; a tap on a side dot glides that
     project to the centre. */
  const onItemClick = useCallback((i: number) => {
    const st = s.current;
    const focusedInt = clamp(Math.round(st.view), 0, N - 1);
    if (i === focusedInt) {
      openProject(i);
    } else {
      markInteracted();
      st.target = i; /* an index is always in [0, N-1] */
    }
  }, [openProject, markInteracted]);

  /* Wheel / trackpad — non-passive so Safari won't rubber-band the page.
     Direct drive: your fingers turn the ring — scroll
     speed IS animation speed, and the morph springs ride along. The
     one-at-a-time rule lives in the physics, not a trigger: a gesture is
     anchored to the slot it began on, the first slot of travel is 1:1, and
     past it the ring rubber-bands progressively harder — so a violent flick
     can lean toward the next-but-one project without ever reaching it.
     The handoff is what makes it fluid: the instant a flick's deltas start
     decaying (the fingers have left — only inertia remains), the gesture
     commits to the adjacent project and the settle spring takes over WITH
     the scroll's velocity, swallowing the rest of the inertia tail. No
     waiting for the tail to go quiet, no mid-slot stall. A gesture that
     never shows a decay (slow deliberate scroll) commits when its events
     stop: past a fifth-slot of travel it advances, otherwise it springs
     home. Discrete mouse wheels — large sparse deltas, nothing like a
     trackpad's dense ramp — step one project per notch.

     All of the envelope logic — peak, decay, the locked tail's ceiling —
     runs on event VELOCITY (px/ms), never raw per-event deltas. When a
     heavy frame makes the browser coalesce two wheel events into one, the
     delta doubles but so does the gap, so velocity stays on the gesture's
     true curve. Raw deltas read every coalesced event as a sudden rise,
     which both stalled the decay commit (the "rise" kept resetting it, so
     modest swipes sprang back home) and let a violent fling's tail break
     out of its lock and step a second slot. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const IDLE_MS = 120;   /* silence that ends a gesture */
    const COMMIT = 0.19;   /* net slots of travel that commit to the next */
    const FLICK = 2.4;     /* gesture speed, slots/s, that commits on its own */
    const FLICK_MIN = 0.14; /* …but only once it has travelled this far */
    const SENS = 0.5;      /* scroll px per slot, as a fraction of drag's 1:1 */
    const SLOT_MAX = 720;  /* px per slot stops tracking the window past this —
                              a swipe is the same effort at any viewport size */
    let active = false;
    let discrete = false;
    let locked = false;    /* committed; swallowing the rest of the tail */
    let lockedDir = 0;
    let anchor = 0;        /* the slot this gesture steps from */
    let viewStart = 0;     /* where the view actually was at gesture start */
    let peakV = 0;         /* px/ms */
    let prevV = 0;
    let decayCount = 0;
    let riseCount = 0;
    let env = Infinity;    /* the locked tail's decaying ceiling, px/ms */
    let lockExcess = 0;    /* slots driven by above-ceiling input while locked */
    let wVel = 0;          /* scroll velocity, slots/s, for the spring seed */
    let flickOk = false;   /* the gesture hit flick speed at some point */
    let lastEvent = 0;
    let lastNotch = 0;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    /* Travel is measured from where the view WAS, not from the anchor slot:
       a re-swipe can begin while the previous settle is still mid-flight, and
       the new swipe's effort must count from there — otherwise it's spent
       catching up to the slot it was already heading for and reads as a dead
       scroll. The anchor stays the logical from-slot, so a committed step is
       always exactly one project. */
    const endGesture = () => {
      const st = s.current;
      active = false;
      if (discrete || locked) return;
      const travel = st.view - viewStart;
      const step = Math.abs(travel) > COMMIT ? anchor + Math.sign(travel) : anchor;
      st.target = clamp(step, 0, N - 1); /* can't step off either end */
    };

    const commit = (dir: number) => {
      const st = s.current;
      locked = true;
      lockedDir = dir;
      const dest = clamp(anchor + dir, 0, N - 1);
      st.target = dest;
      /* the swipe's momentum carries on — but capped low enough that the
         handoff reads as a glide, not a yank — and never into a wall */
      st.viewVel = dest === anchor ? 0 : clamp(wVel, -2.2, 2.2);
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const st = s.current;
      const now = performance.now();
      const gap = now - lastEvent;
      lastEvent = now;
      let delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (e.deltaMode === 1) delta *= 33;        /* lines → px */
      else if (e.deltaMode === 2) delta *= 300;  /* pages → px */
      const abs = Math.abs(delta);
      const dtn = clamp(gap, 4, 60);   /* event spacing, coalescing-aware */
      const v = abs / dtn;             /* px/ms — the envelope's currency */
      const slotPx = clamp(st.gap, 120, SLOT_MAX) * SENS;

      const begin = () => {
        active = true;
        locked = false;
        discrete = abs >= 80; /* a notch, not a finger ramp */
        anchor = clamp(st.target != null ? st.target : Math.round(st.view), 0, N - 1);
        viewStart = st.view;
        peakV = 0;
        prevV = 0;
        decayCount = 0;
        riseCount = 0;
        env = Infinity;
        lockExcess = 0;
        wVel = 0;
        flickOk = false;
      };
      if (!active || gap > IDLE_MS) begin();
      clearTimeout(idleTimer);
      idleTimer = setTimeout(endGesture, IDLE_MS);

      if (discrete) {
        if (gap <= 40) {
          /* Dense follow-ups mean a finger, not a notch — the first event was
             just a fast-starting swipe. Reclassify and drive directly. */
          discrete = false;
        } else {
          if (abs >= 80 && now - lastNotch >= 200) {
            stepBy(delta > 0 ? 1 : -1);
            markInteracted();
            lastNotch = now;
          }
          return;
        }
      }

      if (locked) {
        /* Post-commit tail. One gesture must never step two projects, so this
           lock is deliberately hard to break: it swallows the whole inertia
           tail — AND the run-out of a long continuous scroll that decay-
           committed early — and only a genuinely NEW push takes the ring back.
           Inertia only ever decays, so `env` — the tail's own level, ratcheting
           downward — is a ceiling nothing but real input can cross. Three ways
           out, each demanding fresh intent: a direction flip; a real re-
           acceleration back above HALF the fling's own peak (a decaying tail or
           a run-out that already triggered the commit can't climb back there,
           so neither can chip a second slot); or a sustained deliberate re-
           scroll — driven attenuated by its excess above the ceiling, and only
           owning the ring once that excess totals a seventh of a slot (was a
           twentieth, which a fling's brief tail could reach and double-step).
           True tail events sit at the ceiling, contribute nothing, get eaten. */
        const flip = Math.sign(delta) !== lockedDir && v > 0.4;
        riseCount = v > prevV * 1.25 && v > Math.max(0.7, peakV * 0.5) ? riseCount + 1 : 0;
        prevV = v;
        env = Math.min(env, v);
        if (flip || riseCount >= 2) {
          begin();
        } else {
          const excess = (v - (env * 1.3 + 0.12)) * dtn; /* px above the ceiling */
          if (excess <= 0) return;
          const dvx = (Math.sign(delta) * excess) / slotPx;
          st.view = clamp(st.view + dvx, -0.3, N - 1 + 0.3);
          lockExcess += Math.abs(dvx);
          if (lockExcess > 0.14) begin();
          else return;
        }
      }

      st.target = null; /* fingers own the ring until the gesture commits */
      let dv = delta / slotPx;
      const off = st.view + dv - anchor;
      const over = Math.abs(off) - 1;
      if (over > 0 && Math.sign(dv) === Math.sign(off)) {
        dv /= 1 + over * 10; /* rubber band past one slot out */
      }
      /* Hard ends: the track doesn't loop, so pulling before the first work or
         past the last meets a stiff wall that gives a little and springs back. */
      const next = st.view + dv;
      if (next < 0 && dv < 0) dv /= 1 + (-next) * 20;
      else if (next > N - 1 && dv > 0) dv /= 1 + (next - (N - 1)) * 20;
      st.view += dv;
      wVel = wVel * 0.5 + (dv / (dtn / 1000)) * 0.5;

      if (v > peakV) {
        peakV = v;
        decayCount = 0;
      } else if (v <= prevV * 1.05) {
        decayCount++;
      } else {
        decayCount = 0;
      }
      prevV = v;

      const travel = st.view - viewStart;
      /* Intent is distance OR speed: enough net travel, or a genuinely quick
         flick that has clearly picked a side — a light flick shouldn't need
         to grind out the full commit distance, but casual drift must never
         qualify. Speed is judged at the gesture's PEAK and latched: by the
         time the fingers-off gate opens, wVel has already faded, and asking
         for both in the same event made the commit window one event wide —
         the same physical nudge would randomly switch or spring back on a
         millisecond of event jitter. */
      if (Math.abs(wVel) > FLICK && Math.abs(travel) > FLICK_MIN) flickOk = true;
      const meant = Math.abs(travel) > COMMIT || flickOk;
      /* Fingers off, inertia decaying, and we're past the point of intent:
         hand the ring to the spring now instead of creeping out the tail.
         The decay gate wants real evidence of a lift — three straight fading
         events at under half the gesture's peak — because a mid-scroll
         hesitation looks like the start of a tail, and handing the ring away
         while fingers are still down advances a project nobody asked for. */
      if (meant && decayCount >= 3 && v < peakV * 0.5 && peakV > 0.5) {
        commit(Math.sign(travel));
        env = v;       /* the tail's ceiling starts at the committing speed */
        lockExcess = 0;
        return;
      }
      if (Math.abs(travel) > 0.05) markInteracted();
    };
    root.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      clearTimeout(idleTimer);
      root.removeEventListener('wheel', onWheel);
    };
  }, [stepBy, markInteracted]);

  /* Touch — the mobile counterpart of the wheel gesture. Your finger turns the
     ring directly (drag speed IS animation speed) and, like the wheel, a single
     swipe steps at most one project: past a slot out the ring rubber-bands, and
     on lift the gesture commits to the adjacent work if it travelled far enough
     OR flicked fast enough, else it springs home — the settle spring taking over
     WITH the swipe's velocity so the handoff glides. Swipe left for the next
     work, right for the previous. Only near-horizontal drags rotate; a vertical
     drag is left alone. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const SENS = 0.62;      /* drag px per slot, as a fraction of 1:1 — a touch
                               swipe covers less screen than a trackpad flick */
    const SLOT_MAX = 720;   /* px per slot stops tracking the window past this */
    const COMMIT = 0.19;    /* net slots of travel that commit to the next */
    const FLICK = 2.4;      /* swipe speed, slots/s, that commits on its own */
    const FLICK_MIN = 0.14; /* …but only once it has travelled this far */

    let active = false;
    let axis = 0;           /* 0 undecided, 1 horizontal (rotates), -1 vertical */
    let anchor = 0;         /* the slot this swipe steps from */
    let viewStart = 0;      /* where the view actually was at gesture start */
    let startX = 0, startY = 0;
    let lastX = 0, lastT = 0;
    let vel = 0;            /* swipe velocity, slots/s, smoothed — the spring seed */

    const onStart = (e: TouchEvent) => {
      if (suspendedRef.current || e.touches.length !== 1) return;
      const st = s.current;
      active = true;
      axis = 0;
      const t = e.touches[0];
      startX = lastX = t.clientX;
      startY = t.clientY;
      lastT = performance.now();
      vel = 0;
      anchor = clamp(st.target != null ? Math.round(st.target) : Math.round(st.view), 0, N - 1);
      viewStart = st.view;
      st.target = null; /* the finger owns the ring until the swipe commits */
    };

    const onMove = (e: TouchEvent) => {
      if (!active) return;
      const st = s.current;
      const t = e.touches[0];
      if (axis === 0) {
        const adx = Math.abs(t.clientX - startX);
        const ady = Math.abs(t.clientY - startY);
        if (adx > 8 || ady > 8) axis = adx >= ady ? 1 : -1;
      }
      if (axis !== 1) return;
      e.preventDefault();
      const now = performance.now();
      const dt = clamp(now - lastT, 4, 60);
      lastT = now;
      const slotPx = clamp(st.gap, 120, SLOT_MAX) * SENS;
      let dv = -(t.clientX - lastX) / slotPx; /* swipe left → next work */
      lastX = t.clientX;
      /* Rubber-band past one slot from the anchor, and against the hard ends. */
      const off = st.view + dv - anchor;
      const over = Math.abs(off) - 1;
      if (over > 0 && Math.sign(dv) === Math.sign(off)) dv /= 1 + over * 10;
      const next = st.view + dv;
      if (next < 0 && dv < 0) dv /= 1 + (-next) * 20;
      else if (next > N - 1 && dv > 0) dv /= 1 + (next - (N - 1)) * 20;
      st.view += dv;
      vel = vel * 0.5 + (dv / (dt / 1000)) * 0.5;
      if (Math.abs(st.view - viewStart) > 0.02) markInteracted();
    };

    const onEnd = () => {
      if (!active) return;
      active = false;
      if (axis !== 1) return;
      const st = s.current;
      const travel = st.view - viewStart;
      const flick = Math.abs(vel) > FLICK && Math.abs(travel) > FLICK_MIN;
      const dir = travel !== 0 ? Math.sign(travel) : Math.sign(vel);
      const step = Math.abs(travel) > COMMIT || flick ? anchor + dir : anchor;
      const dest = clamp(step, 0, N - 1);
      st.target = dest;
      /* Hand the ring to the settle spring carrying the swipe's momentum — but
         capped so it glides rather than yanks, and never into a wall. */
      st.viewVel = dest === anchor ? 0 : clamp(vel, -2.2, 2.2);
    };

    root.addEventListener('touchstart', onStart, { passive: true });
    root.addEventListener('touchmove', onMove, { passive: false });
    root.addEventListener('touchend', onEnd);
    root.addEventListener('touchcancel', onEnd);
    return () => {
      root.removeEventListener('touchstart', onStart);
      root.removeEventListener('touchmove', onMove);
      root.removeEventListener('touchend', onEnd);
      root.removeEventListener('touchcancel', onEnd);
    };
  }, [markInteracted]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (suspendedRef.current) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        markInteracted();
        stepBy(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        markInteracted();
        stepBy(-1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (document.activeElement === document.body) {
          e.preventDefault();
          openProject(s.current.focused);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [stepBy, markInteracted, openProject]);

  /* Fit each live embed's fixed desktop viewport into the card and keep it there.
     This is deliberately NOT in the rAF loop: the scale only changes when the
     viewport does, and the loop's resize pass fires once — racing the iframe's
     mount would leave it unscaled (natural 1440×960, so the card shows only its
     top-left). A ResizeObserver re-fits on every resize and, crucially, fires
     once on observe, so the fit is set as soon as the iframe exists (natural
     1600×960, so an unscaled iframe would show only its top-left). `contain`
     (min) times EMBED_ZOOM sets how big the page sits in the card; centred, so
     the zoom-out margin and any off-3:2 letterbox sit evenly in the matte. */
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const fit = () => {
      const { cardW, cardH } = cardMetrics(root.clientWidth, root.clientHeight);
      const contain = Math.min(cardW / EMBED_W, cardH / EMBED_H);
      embedRefs.current.forEach((ef, i) => {
        if (!ef) return;
        const p = PROJECTS[i];
        let scale = contain * (p.embedZoom ?? EMBED_ZOOM);
        let ty = 0; /* screen-px vertical shift; negative clips the top, positive the bottom */
        const cropTop = p.embedCropTop ?? 0;
        const cropBottom = p.embedCropBottom ?? 0;
        if (cropTop > 0) {
          /* Grow the page until it stands `cropTop` taller than the card, then pin
             its bottom to the card's bottom — so exactly that top band is clipped
             and nothing mattes in below. */
          scale = Math.max(scale, (cardH * (1 + cropTop)) / EMBED_H);
          ty = -(EMBED_H * scale - cardH) / 2;
        } else if (cropBottom > 0) {
          /* The mirror: grow the page until it stands `cropBottom` taller than the
             card, then pin its top to the card's top — so exactly that bottom band
             is clipped and nothing mattes in above. */
          scale = Math.max(scale, (cardH * (1 + cropBottom)) / EMBED_H);
          ty = (EMBED_H * scale - cardH) / 2;
        }
        ef.style.transform = `translate(-50%, -50%) translateY(${ty.toFixed(1)}px) scale(${scale.toFixed(4)})`;
      });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  /* The loop. Physics advances the view; then each work is placed by its angle
     around the ellipse. `phi` is how far it sits from dead-ahead; `facing`
     (cos phi) is 1 when you're looking straight at it and falls to -1 behind you.
     Horizontal seat is A·sin(phi) so planets slide out to the rim and back;
     a shallow vertical arc lifts them as they recede, selling the oval. `bloom`
     (a smoothstep of centredness) is the target the per-item size springs chase:
     the black shape opens from a dot into the full card with overshoot and
     settle, and the photo lifts in trailing it — so a dot doesn't just grow, it
     morphs — while `facing` fades a planet out as it rotates behind you. */
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    s.current.reduced = mq.matches;
    const onMq = () => { s.current.reduced = mq.matches; };
    mq.addEventListener?.('change', onMq);

    /* On mobile the side dots are dropped for a single-card swipe pager — see the
       opacity below. Tracks the same 640px breakpoint the CSS uses. */
    const mqMobile = window.matchMedia('(max-width: 640px)');
    s.current.mobile = mqMobile.matches;
    const onMqMobile = () => { s.current.mobile = mqMobile.matches; };
    mqMobile.addEventListener?.('change', onMqMobile);

    let raf = 0;
    /* The photo and shadow layers only need their pixel box set when the viewport
       changes, so we cache the last card size and rewrite those (and z-index /
       pointer-events) only when they actually move. */
    let lastCardW = -1;
    let lastCardH = -1;
    const zCache = new Array(N).fill(NaN);
    const peCache = new Array(N).fill('');

    /* Dynamic-Island springs — a width/height pair per work. The geometric
       bloom (a pure function of scroll position) is only ever the spring
       *target*; the painted size is the spring state, so it lags a moving
       target, overshoots a settling one and wobbles once before rest.
       ζ = c / (2√k). The lead role swaps with direction so the shape passes
       through a WIDE capsule both ways (the previews are landscape):
       opening, width is stiff and leads (ζ 0.67) while height trails looser
       and gives the visible bounce (ζ 0.57); closing, HEIGHT is the stiff
       one and falls first — the card melts through a wide pill, like the
       Island narrowing before it contracts — while width trails after
       (both near-critical, ζ 0.90/0.88, so it shuts without clatter). */
    const W_OPEN_K = 380, W_OPEN_C = 26, W_CLOSE_K = 200, W_CLOSE_C = 25;
    const H_OPEN_K = 200, H_OPEN_C = 16, H_CLOSE_K = 380, H_CLOSE_C = 35;
    const bw = new Float64Array(N), bwVel = new Float64Array(N);
    const bh = new Float64Array(N), bhVel = new Float64Array(N);
    let springsReady = false;
    let stretch = 0;   /* smoothed momentum-stretch of travelling dots */
    let lastView = 0;
    let lastT = 0;

    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const root = rootRef.current;
      if (!root) return;
      const st = s.current;
      const vw = root.clientWidth;
      const vh = root.clientHeight;

      if (!st.introStart) st.introStart = now;
      const intro = st.reduced ? 1 : easeOutCubic(Math.min(1, (now - st.introStart) / 600));
      /* The intro is a gentle in-place settle carried by motion, not a fade: the
         card eases up the last tenth into full size while opacity only firms in
         from a high floor. Both start close to their resting value, so nothing
         blooms open or blinks — index 0 simply arrives. introScale rides the
         item's own transform (compositor-only), NOT the morph's width/height:
         resizing the morph re-lays-out and repaints the full-size photo and the
         blurred shadow every frame, which stutters exactly when first paint is
         busiest (image decode, embed loads). As a transform, the settle costs
         the GPU a matrix and the morph geometry stays bit-identical throughout. */
      const introScale = st.reduced ? 1 : 0.9 + 0.1 * intro;
      const introFade = st.reduced ? 1 : 0.8 + 0.2 * intro;

      /* Real frame time so the springs behave the same at 60 and 120 Hz;
         clamped so a background tab doesn't come back with an explosive step. */
      const dt = lastT ? Math.min(0.032, Math.max(0.001, (now - lastT) / 1000)) : 1 / 60;
      lastT = now;

      /* --- physics ---
         Every gesture resolves to a whole-slot target; there is no free
         coasting, so the ring only ever travels one project at a time. */
      if (st.target != null) {
        const diff = st.target - st.view;
        if (st.reduced) {
          st.view += diff * 0.3;
          if (Math.abs(diff) < 0.0006) {
            st.view = st.target;
            st.target = null;
          }
        } else {
          /* Underdamped spring (ζ≈0.85): the arriving work keeps its
             momentum, grazes just past centre and is drawn back — it never
             dead-stops, but it doesn't lunge either. */
          st.viewVel += (diff * 100 - st.viewVel * 17) * dt;
          st.view += st.viewVel * dt;
          if (Math.abs(diff) < 0.0005 && Math.abs(st.viewVel) < 0.004) {
            st.view = st.target;
            st.target = null;
            st.viewVel = 0;
          }
        }
      }

      /* --- geometry ---
         The full-size still fills most of the stage; a receding planet is a
         small circle. `A` is the ellipse's horizontal reach (how far to the rim),
         `arcY` its far shallower vertical rise, so the orbit reads as a wide oval
         seen from just above. */
      const { cardW, cardH } = cardMetrics(vw, vh);
      const dot = clamp(vw * 0.012, 12, 18);
      const slotAngle = (Math.PI * 2) / N;
      const A = clamp(vw * 0.44, cardW * 0.52 + 40, vw * 0.48);
      const arcY = Math.min(A * 0.13, vh * 0.13);
      /* Headroom kept clear of .orbit's clipped edge for the height spring's
         own overshoot (it's deliberately underdamped — see H_OPEN_K/C below). */
      const V_MARGIN = 12;
      /* Pixels per slot at the front, where the rim is moving fastest across the
         screen — keeps a drag tracking the pointer 1:1 as you rotate. */
      st.gap = A * slotAngle;

      /* Resize-only writes: the photo and shadow layers are painted at their full
         natural size once and thereafter only transform-scaled, so their heavy
         content (the source image, the blurred shadow) is rasterised a single
         time. Only the black morph shape actually changes size per frame. */
      if (cardW !== lastCardW || cardH !== lastCardH) {
        lastCardW = cardW;
        lastCardH = cardH;
        for (let i = 0; i < N; i++) {
          const im = imgRefs.current[i];
          if (im) { im.style.width = `${cardW}px`; im.style.height = `${cardH}px`; }
          const sh = shadowRefs.current[i];
          if (sh) { sh.style.width = `${cardW}px`; sh.style.height = `${cardH}px`; }
        }
      }

      /* No lateral spin-up on load: the ring sits on the focused slot from the
         very first frame, so nothing sweeps in from the side. The intro is a
         gentle in-place settle — a soft scale-up with only a whisper of fade
         (introScale / introFade below) — so the work eases into centre at index
         0 rather than sliding, blooming from a dot, or blinking in. */
      const viewRender = st.view;

      /* How fast the ring is turning, smoothed. A travelling dot is stretched
         along its path and squashed across it — a drop in flight — and
         relaxes back to a circle as the ring slows. */
      if (!springsReady) lastView = viewRender;
      const slotsPerSec = (viewRender - lastView) / Math.max(dt, 0.008);
      lastView = viewRender;
      const stretchT = st.reduced ? 0 : clamp(Math.abs(slotsPerSec) * 0.05, 0, 0.3);
      stretch += (stretchT - stretch) * Math.min(1, dt * 14);

      for (let i = 0; i < N; i++) {
        const el = itemRefs.current[i];
        if (!el) continue;
        const morph = morphRefs.current[i];
        const fill = fillRefs.current[i];
        const img = imgRefs.current[i];
        const shadow = shadowRefs.current[i];
        const d = drawOffset(i - viewRender);
        const ad = Math.abs(d);
        const phi = d * slotAngle;                 /* angle off dead-ahead */
        const facing = Math.cos(phi);              /* 1 straight-on → -1 behind you */
        const centre = clamp(1 - ad, 0, 1);        /* 1 dead-centre → 0 by one slot out */
        const bloom = centre * centre * (3 - 2 * centre); /* smoothstep: the spring target */

        /* Integrate the size springs (semi-implicit Euler, mass 1) toward the
           bloom the position asks for. Each axis picks its constants by its
           own direction of travel, which is what swaps the lead: opening,
           width outruns height; closing, height outruns width — the shape is
           always wider than tall mid-morph. */
        if (!springsReady || st.reduced) {
          bw[i] = bloom; bwVel[i] = 0;
          bh[i] = bloom; bhVel[i] = 0;
        } else {
          const wOpen = bloom > bw[i];
          bwVel[i] += (-(wOpen ? W_OPEN_K : W_CLOSE_K) * (bw[i] - bloom) - (wOpen ? W_OPEN_C : W_CLOSE_C) * bwVel[i]) * dt;
          bw[i] += bwVel[i] * dt;
          const hOpen = bloom > bh[i];
          bhVel[i] += (-(hOpen ? H_OPEN_K : H_CLOSE_K) * (bh[i] - bloom) - (hOpen ? H_OPEN_C : H_CLOSE_C) * bhVel[i]) * dt;
          bh[i] += bhVel[i] * dt;
        }
        const openness = clamp((bw[i] + bh[i]) / 2, 0, 1);

        const x = A * Math.sin(phi);               /* seat along the rim */
        const y = -arcY * (1 - facing);            /* rise as it curves away */
        const bob = st.reduced ? 0 : Math.sin(now / 1600 + i * 1.7) * 5 * (1 - openness) * clamp(facing, 0, 1);

        /* The one shape that morphs: a solid black rounded box growing from a
           dot-sized circle into the full card — its size is the spring state,
           so it stretches wide first, blooms tall, overshoots and settles.
           Cheap to resize — no image, no blur — so it can change every frame.
           `squash` elongates a travelling dot along its path. */
        const squash = stretch * (1 - clamp(bw[i], 0, 1));
        const mw = Math.max(2, (dot + (cardW - dot) * bw[i]) * (1 + squash));
        const rawMh = Math.max(2, (dot + (cardH - dot) * bh[i]) * (1 - squash * 0.55));
        /* The bloom's peak can overshoot past the resting card height on the way
           in; clamp to whatever actually fits between this item's seat (y + bob)
           and the viewport edges so the overshoot never punches through
           .orbit's overflow:hidden and reads as a cropped card. */
        const mh = Math.min(rawMh, Math.max(2, vh - 2 * Math.abs(y + bob) - V_MARGIN));
        /* Continuous curvature, like the Island: a capsule (radius = half the
           short side) while small, its roundness decaying into the card's 16px
           as it opens — never a sudden change of curve. */
        const capsuleR = Math.min(mw, mh) / 2;
        const radius = Math.min(capsuleR, 16 + (capsuleR - 16) * Math.pow(1 - openness, 1.5));
        /* The photo, fixed at full size, scaled to be *contained* within the card
           and centred on it — the whole image always fits inside the morph at its
           natural aspect, and the white matte (the morph's own background) fills
           the margins. It lifts in trailing the shape — content follows the
           container, Island-style. */
        const scaleX = mw / cardW;
        const scaleY = mh / cardH;
        /* Contain: scale the photo layer down by whichever axis is tighter so it
           sits fully inside the morph rather than over-covering and cropping. */
        const fit = Math.min(scaleX, scaleY);
        const reveal = clamp((openness - 0.1) * 1.45, 0, 1);
        /* Desktop: planets sit solid black at the sides (facing 0) and fade only
           as they swing behind you. Mobile: no side dots — an item is visible only
           as its photo reveals, so a receding neighbour fades out instead of
           parking as a black dot; you see just the centred card and swipe one
           project at a time. */
        const opacity = (st.mobile
          ? reveal
          : clamp((facing + 0.45) / 0.45, 0, 1)) * introFade;

        el.style.transform = `translate3d(${x.toFixed(1)}px, ${(y + bob).toFixed(1)}px, 0) scale(${introScale.toFixed(4)})`;
        el.style.opacity = opacity.toFixed(3);

        if (morph) {
          morph.style.width = `${mw.toFixed(1)}px`;
          morph.style.height = `${mh.toFixed(1)}px`;
          morph.style.borderRadius = `${radius.toFixed(1)}px`;
        }
        if (fill) {
          /* The black dot: an opaque black overlay covering the whole card while
             the work is a small receding disc, fading out as it opens (reveal → 1)
             to uncover the preview and its matte together. Because it sits above
             the preview, the fade darkens the preview and its frame uniformly —
             a black planet crossfading evenly into the framed card. */
          fill.style.opacity = (1 - reveal).toFixed(3);
        }
        if (img) {
          img.style.transform = `translate(-50%, -50%) scale(${fit.toFixed(4)})`;
          img.style.opacity = reveal.toFixed(3);
        }
        if (shadow) {
          shadow.style.transform = `translate(-50%, -50%) scale(${scaleX.toFixed(4)}, ${scaleY.toFixed(4)})`;
          /* Match the shadow's corners to the morph's capsule. The shadow is a
             fixed cardW×cardH layer that's only ever transform-scaled (so its
             blur never repaints), so its border-radius lives in pre-scale space:
             counter-scale each axis by (radius / scale) so that after the
             non-uniform scale it renders as the SAME circular `radius` the morph
             has. Without this the shadow stays a squarish 16px rect while the
             shape blooms into a capsule, and the blank page shows through the
             gap at the corners. */
          shadow.style.borderRadius = `${(radius / scaleX).toFixed(2)}px / ${(radius / scaleY).toFixed(2)}px`;
          shadow.style.opacity = openness.toFixed(3);
        }

        const zi = 1000 + Math.round(facing * 100);
        if (zCache[i] !== zi) { zCache[i] = zi; el.style.zIndex = String(zi); }
        const pe = opacity > 0.06 ? 'auto' : 'none';
        if (peCache[i] !== pe) { peCache[i] = pe; el.style.pointerEvents = pe; }
      }

      springsReady = true;

      const f = clamp(Math.round(st.view), 0, N - 1);
      if (f !== st.focused) {
        st.focused = f;
        setFocused(f);
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      mq.removeEventListener?.('change', onMq);
      mqMobile.removeEventListener?.('change', onMqMobile);
    };
  }, []);

  return (
    <div ref={rootRef} className="orbit">
      <div className="orbit-stage">
        {PROJECTS.map((p, i) => (
          <button
            key={p.title}
            className="orbit-item"
            ref={(el) => { itemRefs.current[i] = el; }}
            onClick={() => onItemClick(i)}
            aria-label={`${p.title}, ${p.year}`}
            tabIndex={-1}
          >
            <span className="orbit-shadow" ref={(el) => { shadowRefs.current[i] = el; }} aria-hidden="true" />
            <span className="orbit-morph" ref={(el) => { morphRefs.current[i] = el; }} style={p.bg ? { background: p.bg } : undefined}>
              <span className="orbit-img" ref={(el) => { imgRefs.current[i] = el; }}>
                {p.embed ? (
                  /* No poster still here: the page is inset by EMBED_ZOOM, so a
                     full-bleed still would peek around it. The morph's matte
                     fills the frame until the live page paints in. The wrapper
                     carries the JS fit/crop transform; the iframe inside carries
                     only the CSS hover scale, so the two compose the way the
                     span/img split does for the photo previews. */
                  <span
                    className="orbit-embed-fit"
                    ref={(el) => { embedRefs.current[i] = el; }}
                  >
                    <iframe
                      className="orbit-embed"
                      src={embedsLive ? p.embed : undefined}
                      title={p.title}
                      tabIndex={-1}
                      aria-hidden="true"
                      scrolling="no"
                      loading="lazy"
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </span>
                ) : (
                  <img src={p.image} alt="" decoding="async" style={{ objectPosition: p.position, objectFit: p.fit ?? 'contain' }} draggable={false} />
                )}
              </span>
              {/* The black dot sits ABOVE the preview so, as it fades in on close,
                 it darkens the preview and its matte frame by the same factor —
                 the whole card collapses to black as one uniform shape rather than
                 the preview staying brighter than the frame around it. */}
              <span className="orbit-fill" ref={(el) => { fillRefs.current[i] = el; }} aria-hidden="true" />
            </span>
          </button>
        ))}
      </div>

      <header className="orbit-hud">
        <h1 className="orbit-name">Leo Buckman</h1>
        <div className="orbit-contact">
          <a href="mailto:leo.s.buckman@gmail.com">Email</a>
          <span className="orbit-contact-divider" aria-hidden="true" />
          <a href="https://linkedin.com/in/leobuckman">LinkedIn</a>
        </div>
      </header>

      <div className="orbit-placard" aria-live="polite">
        <div className="orbit-placard-inner" key={focused}>
          <span className="orbit-placard-title">{PROJECTS[focused].title}</span>
          <span className="orbit-placard-year">{PROJECTS[focused].year}</span>
        </div>
      </div>

      <div className="orbit-counter" aria-hidden="true">
        {String(focused + 1).padStart(2, '0')} — {String(N).padStart(2, '0')}
      </div>

    </div>
  );
};

export default Orbit;
